import React, { useState, useEffect } from 'react';

// 1. CHỈ IMPORT ĐÍCH DANH CÁC COMPONENT CẦN DÙNG
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import ContainerGrid from "./ContainerGrid";
import { loadBabelOnce } from "../utils";
import { useQuickAction } from "./DataQuickActionContext";//Tạo nhanh chứng từ khác từ trong script danh sách
import { bienDichComponent } from './bienDichJsx';//Biên dịch script JSX -> component (an toàn với hàm phụ Babel chèn đầu mã)

// =====================================================================
// COMPONENT LÕI: TRÌNH BIÊN DỊCH LAI (HYBRID RUNTIME COMPILER)
// =====================================================================
const DynamicCustomList = ({
  script,
  dataList = [],
  handleEdit,
  handleDelete,
  handleCopy,
  // MỞ TRANG CHI TIẾT. Trước đây script danh sách tuỳ biến chỉ được cấp sửa/xoá/nhân bản, nên với
  // một model CHỈ ĐỌC (vd bài post Facebook đồng bộ về) không có cách nào mở trang chi tiết — buộc
  // phải nhét hết nội dung vào thẻ, và danh sách dài vô tận.
  handleView,
  context
}) => {
  // quickAction: dùng DataQuickActionContext (KHÔNG dùng context.quickAdd) để tạo nhanh chứng từ tiếp theo
  const quickAction = useQuickAction();
  const [CompilerConfig, setCompilerConfig] = useState({ 
    RootComponent: null, 
    MUI: null, 
    error: null, 
    isLegacy: false, // Flag đánh dấu script cũ hay mới
    isLoading: true 
  });

  useEffect(() => {
    let isMounted = true;
    
    const compileScript = async () => {
      try {
        setCompilerConfig(prev => ({ ...prev, isLoading: true, error: null }));

        // 2. TẢI ĐỘNG BABEL VÀ MUI
        const loadBabel = loadBabelOnce();
        const [Babel, DynamicallyLoadedMUI] = await Promise.all([
          loadBabel,
          import('@mui/material') 
        ]);

        // 3. NHẬN DIỆN LOẠI SCRIPT (CŨ HOẶC MỚI)
        // Nếu script chứa "dataList" hoặc ".map", ta coi đó là script build nguyên danh sách (Mới)
        // Nếu không có, ta coi đó là script build từng Item lẻ (Cũ)
        const isLegacyScript = !script.includes("dataList");

        let wrappedScript = "";

        if (isLegacyScript) {
          // --- MÔI TRƯỜNG CHẠY SCRIPT CŨ (Tự bọc Fragment + Ép cấu trúc hàm nhận Single Item) ---
          let scriptBody = script;
          if (!scriptBody.includes("return ")) {
              scriptBody = "return (\n<React.Fragment>\n" + scriptBody + "\n</React.Fragment>\n);";
          }
          wrappedScript = 
            "(function CustomListItem(props) {\n" +
            "  const { MUI, React, item, index, handleEdit, handleDelete, handleCopy, handleView, context, quickAction } = props;\n" +
            "  " + scriptBody + "\n" +
            "})";
        } else {
          // --- MÔI TRƯỜNG CHẠY SCRIPT MỚI (Nhận nguyên dataList để tự quẫy Layout) ---
          let scriptBody = script;
          if (!scriptBody.includes("return ")) {
              scriptBody = "return (\n<React.Fragment>\n" + scriptBody + "\n</React.Fragment>\n);";
          }
          wrappedScript = 
            "(function CustomListContainer(props) {\n" +
            "  const { MUI, React, ContainerGrid, dataList, handleEdit, handleDelete, handleCopy, handleView, context, quickAction } = props;\n" +
            "  " + scriptBody + "\n" +
            "})";
        }

        // 5. DỊCH JSX + KHỞI TẠO COMPONENT (xem bienDichJsx: không ghép `return ${ma}`)
        const GeneratedComponent = bienDichComponent(Babel, wrappedScript, ['React', 'MUI', 'ContainerGrid'], [React, DynamicallyLoadedMUI, ContainerGrid]);

        if (isMounted) {
            setCompilerConfig({ 
                RootComponent: GeneratedComponent, 
                MUI: DynamicallyLoadedMUI, 
                isLegacy: isLegacyScript,
                error: null, 
                isLoading: false 
            });
        }
      } catch (err) {
        console.error("Lỗi biên dịch Custom List/Item:\n", script, err);
        if (isMounted) setCompilerConfig({ RootComponent: null, MUI: null, isLegacy: false, error: err.message, isLoading: false });
      }
    };

    if (script) {
        compileScript();
    } else {
        if (isMounted) setCompilerConfig({ RootComponent: null, MUI: null, isLegacy: false, error: null, isLoading: false });
    }
    
    return () => { isMounted = false; };
  }, [script]);

  // Trạng thái hiển thị Loading & Error
  if (CompilerConfig.isLoading) return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 3, justifyContent: 'center' }}>
      <CircularProgress size={24} />
      <Typography color="text.secondary">Đang cấu hình giao diện danh sách...</Typography>
    </Box>
  );

  if (CompilerConfig.error) return (
    <Alert severity="error" sx={{ m: 2, borderRadius: 2 }}>
      <strong>Lỗi trình biên dịch giao diện:</strong> {CompilerConfig.error}
    </Alert>
  );

  const { RootComponent, MUI, isLegacy } = CompilerConfig;
  if (!RootComponent || !MUI) return null;

  // 6. PHÂN LUỒNG RENDER DỰA TRÊN LOẠI SCRIPT ĐÃ DIỄN DỊCH
  if (isLegacy) {
    // Nếu là script cũ: Component tổng sẽ tự chạy vòng lặp .map() hộ user giống hệt bản gốc của bạn
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1 }}>
        {dataList.map((item, index) => {
          // Khởi chạy component cũ với các props cũ
          const LegacyItem = RootComponent; 
          return (
            <LegacyItem 
              key={item._id || item.id || index} 
              item={item} 
              index={index}
              handleEdit={() => handleEdit && handleEdit(item)} 
              handleDelete={() => handleDelete && handleDelete(item)} 
              handleCopy={() => handleCopy && handleCopy(item)}
              handleView={() => handleView && handleView(item)}
              context={context}
              quickAction={quickAction}
              MUI={MUI}
              React={React}
            />
          );
        })}
      </Box>
    );
  }

  // Nếu là script mới: Đưa thẳng dataList cho script tự xử lý cấu trúc
  return (
    <RootComponent 
      dataList={dataList}
      handleEdit={handleEdit}
      handleDelete={handleDelete}
      handleCopy={handleCopy}
      handleView={handleView}
      context={context}
      quickAction={quickAction}
      MUI={MUI}
      React={React}
      ContainerGrid={ContainerGrid}
    />
  );
};

export default DynamicCustomList;